import Vue from 'vue'
import Router from 'vue-router'
import store from '@/store'
import { requireAuth, isLoggedIn } from '@/utils/auth'
import { redirectHelpContactUrl } from '@/utils/redirectRebtelCom'
import { Translation } from '@/utils/Translation'
import { updateRedirectToUsersLocale } from '@/utils/redirectLocale'
import { mParticleLogin, trackPageView, setUtmParams, setTikTokParams } from '@/utils/tracking'
import { isNullOrUndefined } from '../utils/comparators'
import { isWorldCredits } from '@/utils/productTypes'
import { fetchCheckoutRedirectConfig } from '@/api/vercelServerless'
import { SUPPORTED_LANGUAGES_IN_NEW_CHECKOUT, DEFAULT_LANGUAGE } from '@/utils/constants'

const Signin = () => import(/* webpackChunkName: "signin" */ '@/views/Signin')
const UnifiedBundles = () => import(/* webpackChunkName: "bundles" */ '@/views/UnifiedBundles')

const Account = () => import(/* webpackChunkName: "account" */ '@/views/Account')
const AccountCancel = () => import(/* webpackChunkName: "account-cancel" */ '@/views/AccountCancel')
const ProfileSettings = () => import(/* webpackChunkName: "account-profile" */ '@/views/ProfileSettings')
const AccountSettings = () => import(/* webpackChunkName: "account-settings" */ '@/views/AccountSettings')
const Activity = () => import(/* webpackChunkName: "activity" */ '@/views/Activity')
const InternationalCalling = () => import(/* webpackChunkName: "communication" */ '@/views/InternationalCalling')
const InternationalCallingDeals = () => import(/* webpackChunkName: "communication" */ '@/views/InternationalCallingDeals')

const Invite = () => import(/* webpackChunkName: "invite" */ '@/views/Invite')
const BonusMinutes = () => import(/* webpackChunkName: "invite" */ '@/views/BonusMinutes')
const RedeemVoucher = () => import(/* webpackChunkName: "redeem-voucher" */ '@/views/RedeemVoucher')
const NotFound = () => import(/* webpackChunkName: "not-found" */ '@/views/NotFound')
const MandaoSelectAmount = () => import(/* webpackChunkName: "bundles" */ '@/views/MandaoSelectAmount')
const CheckoutV2 = () => import(/* webpackChunkName: "checkoutv2" */ '@/views/Checkout-v2')
const CheckoutV2Complete = () => import(/* webpackChunkName: "checkoutv2" */ '@/views/Checkout-v2Complete')
const LocalNumbers = () => import(/* webpackChunkName: "localnumbers" */ '@/views/LocalNumbers')
const TafRedeem = () => import(/* webpackChunkName: "tafRedeem" */ '@/views/TafRedeem')
const TafCheckoutComplete = () => import(/* webpackChunkName: "tafCheckoutComplete" */ '@/views/TafCheckoutComplete')
const RebtelCredits = () => import(/* webpackChunkName: "rebtelCredits" */ '@/views/RebtelCredits')

Vue.use(Router)

const authorizedUser = (to, from, next) => {
  if (isLoggedIn()) {
    store.dispatch('user/loadUser').then(() => {
      const userLocale = store.getters['user/locale']
      let newUrl
      if (to.params.locale !== userLocale) {
        newUrl = updateRedirectToUsersLocale(to.fullPath, userLocale)
      }
      store.dispatch('user/products/loadUserProducts').then(res => next(newUrl))
    }).catch((error) => {
      console.error('loadUser', error)
      next()
    })
  } else {
    requireAuth(to)
  }
}

const redirectOnCheckout = async (to, from, next) => {
  if (isLoggedIn()) {
    try {
      const response = await fetchCheckoutRedirectConfig()
      if (response.shouldRedirect) {
        const userCurrency = store.getters['user/currency'] || 'USD'
        window.$cookies.set('currency', userCurrency, '365d', '/', process.env.COOKIE_DOMAIN)
        const redirectLocale = SUPPORTED_LANGUAGES_IN_NEW_CHECKOUT.includes(to.params.locale) ? to.params.locale : DEFAULT_LANGUAGE
        window.location.href = process.env.WWW_URL + '/' + redirectLocale + '/checkout'
      } else {
        next()
      }
    } catch (error) {
      console.error('Error in newTestGuard navigation guard:', error)
      next()
    }
  } else {
    requireAuth(to)
  }
}

// TAF route guard not allowing paying users into Taf Redeem page
const redirectIfPayingUser = (to, from, next) => {
  if (isLoggedIn()) {
    store.dispatch('user/statistics/loadStatistics').then(() => {
      console.log('redirect guard')
      const hasPaid = store.getters['user/statistics/hasPaid']
      let newUrl
      if (hasPaid) {
        newUrl = '/en/my/account/'
        router.app.$toastr.e({
          'msg': router.app.$t('taf.error_existing_user'), // Message
          'clickClose': true, // Click Close Disable
          'timeout': 0, // Remember defaultTimeout is 5 sec..
          'preventDuplicates': true // Default is false,
        })
      }
      store.dispatch('user/products/loadUserProducts').then(res => next(newUrl))
    }).catch((error) => {
      console.error('tafRedirect', error)
      next()
    })
  } else {
    requireAuth(to)
  }
}

const redirectToSendMoney = () => {
  let url = process.env.MY_URL
  url += '/en/my/send-money/'
  window.location.href = url
}
const redirectToAddMoney = () => {
  let url = process.env.MY_URL
  url += '/en/my/rebtel-credits/'
  window.location.href = url
}
const authorizedUserLegacyCheckout = (to, from, next) => {
  if (isLoggedIn()) {
    store.dispatch('user/loadUser').then(() => {
      const userLocale = store.getters['user/locale']
      let newUrl
      if (to.params.locale !== userLocale) {
        newUrl = updateRedirectToUsersLocale(to.fullPath, userLocale)
      }
      if (to.name === 'LegacyCheckout' || to.name === 'CheckoutAddCard') {
        window.$cookies.set('selectedProduct', {
          id: to.params.pid,
          msisdn: to.params.msisdn ? to.params.msisdn : null,
          legacy: true,
          type: isWorldCredits({id: to.params.pid}) ? 'worldCredit' : 'calling'
        }, '7d', '/', process.env.COOKIE_DOMAIN)
        newUrl = '/en/my/checkout/'
      }
      store.dispatch('user/products/loadUserProducts').then(res => next(newUrl))
    }).catch((error) => {
      console.error('loadUser', error)
      next()
    })
  } else {
    requireAuth(to)
  }
}

const nonAuthorizedUser = (to, from, next) => {
  if (isLoggedIn()) {
    next({ name: 'Account', params: { locale: to.params.locale } })
  } else {
    next()
  }
}

let router = new Router({
  scrollBehavior (to, from, savedPosition) {
    if (to.path === from.path) {
      return
    }
    if (to.hash) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve({
            selector: to.hash
          })
        }, 500)
      })
    }

    const position = savedPosition || { x: 0, y: 0 }

    return position
  },
  mode: 'history',
  routes: [
    {
      path: '/:locale/my/signin/',
      alias: ['/:locale/my/signin/referral/', '/:locale/my/signin/taf/'],
      name: 'Signin',
      meta: {title: 'Sign in'},
      component: Signin,
      beforeEnter: nonAuthorizedUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/referral/redeem/',
      name: 'TafRedeem',
      meta: {title: 'Taf Redeem'},
      component: TafRedeem,
      beforeEnter: redirectIfPayingUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/checkout/',
      name: 'CheckoutV2',
      props: true,
      meta: {
        title: 'Checkout',
        disableSupportWidget: true
      },
      component: CheckoutV2,
      beforeEnter: redirectOnCheckout,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/rebtel-credits/',
      name: 'RebtelCredits',
      meta: {
        title: 'Rebtel Credits'
      },
      component: RebtelCredits,
      beforeEnter: authorizedUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/checkout/:id/',
      name: 'CheckoutV2ID',
      component: CheckoutV2,
      beforeEnter: redirectOnCheckout,
      pathToRegexpOptions: { strict: false }
    },
    {
      path: '/:locale/my/checkout-complete/',
      name: 'CheckoutV2Complete',
      meta: {
        title: 'Checkout Complete',
        disableSupportWidget: true
      },
      component: CheckoutV2Complete,
      beforeEnter: authorizedUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/referral/checkout-complete/',
      name: 'TafCheckoutComplete',
      meta: {
        title: 'Checkout Complete',
        disableSupportWidget: true
      },
      component: TafCheckoutComplete,
      beforeEnter: authorizedUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/account/',
      name: 'Account',
      meta: {title: 'My Account'},
      component: Account,
      beforeEnter: authorizedUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/account/:module/',
      name: 'AccountModule',
      component: Account,
      beforeEnter: authorizedUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/referral/',
      alias: '/:locale/my/invite/',
      name: 'Invite',
      meta: {title: 'Invite a friend'},
      component: Invite,
      beforeEnter: authorizedUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/cancel/',
      name: 'AccountCancel',
      meta: {title: 'Cancel my account'},
      component: AccountCancel,
      beforeEnter: authorizedUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/settings/account/',
      name: 'AccountSettings',
      meta: {title: 'Account settings'},
      component: AccountSettings,
      beforeEnter: authorizedUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/settings/account/:id/',
      name: 'AccountSettingsCheckStatus',
      component: AccountSettings,
      beforeEnter: authorizedUser,
      pathToRegexpOptions: { strict: false }
    },
    {
      path: '/:locale/my/settings/profile/',
      name: 'ProfileSettings',
      meta: {title: 'My profile'},
      component: ProfileSettings,
      beforeEnter: authorizedUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/mandao/',
      name: 'MandaoSelectAmount',
      meta: {
        title: 'Mandao'
      },
      component: MandaoSelectAmount,
      beforeEnter: authorizedUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/international-calling/',
      name: 'InternationalCalling',
      meta: {
        title: 'International calling'
      },
      component: InternationalCalling,
      beforeEnter: authorizedUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/international-calling/bonus/',
      name: 'BonusMinutes',
      meta: {
        title: 'Bonus minutes'
      },
      component: BonusMinutes,
      beforeEnter: authorizedUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/international-calling/deals/',
      name: 'InternationalCallingDeals',
      component: InternationalCallingDeals,
      beforeEnter: authorizedUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/activity/',
      name: 'Activity',
      meta: {
        title: 'Account activity'
      },
      component: Activity,
      beforeEnter: authorizedUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/voucher/',
      name: 'RedeemVoucher',
      component: RedeemVoucher,
      beforeEnter: authorizedUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/local-numbers/*',
      name: 'LocalNumbers',
      meta: {
        title: 'Local numbers'
      },
      component: LocalNumbers,
      beforeEnter: authorizedUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/checkout/product/:pid/signup/',
      name: 'LegacyCheckout',
      beforeEnter: authorizedUserLegacyCheckout,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/checkout/product/:pid/payment/*',
      name: 'CheckoutAddCard',
      beforeEnter: authorizedUserLegacyCheckout,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/creditmodal/',
      name: 'RebtelCreditsLegacyRoute',
      beforeEnter: redirectToAddMoney,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/send-money/',
      name: 'UnifiedBundles',
      meta: { title: 'Send Money' },
      component: UnifiedBundles,
      beforeEnter: authorizedUser,
      props: true,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/send-money/recargas/*',
      name: 'RecargasCubaLegacy',
      meta: { title: 'Send Money' },
      props: { openPrebookingTab: false },
      component: UnifiedBundles,
      beforeEnter: authorizedUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/subscriptions/*',
      name: 'MySubscriptions',
      meta: { title: 'My Subscriptions' },
      props: { openSubscriptionTab: true },
      component: UnifiedBundles,
      beforeEnter: authorizedUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/send-money/prebooking/*',
      name: 'CubaPrebookingLegacy',
      meta: { title: 'Send Money' },
      props: { openPrebookingTab: true },
      component: UnifiedBundles,
      beforeEnter: authorizedUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/prebooking/*',
      name: 'CubaPrebookingLegacyV2',
      meta: { title: 'Send Money' },
      props: { openPrebookingTab: true },
      component: UnifiedBundles,
      beforeEnter: authorizedUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/recargas/*',
      name: 'CubaRecargas',
      meta: { title: 'Send Money' },
      props: { openPrebookingTab: false },
      component: UnifiedBundles,
      beforeEnter: authorizedUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/:locale/my/nauta/*',
      name: 'CubaNauta',
      meta: {
        title: 'Send Money'
      },
      component: UnifiedBundles,
      beforeEnter: authorizedUser,
      pathToRegexpOptions: { strict: true }
    },
    {
      path: '/recargas/:number/',
      name: 'CubaRecargasLegacyV2',
      meta: { title: 'Send Money' },
      beforeEnter (to, from, next) {
        redirectToSendMoney()
      }
    },
    {
      path: '/:locale/recharge/:number/',
      name: 'RecargasWithPhoneNumber',
      meta: { title: 'Send Money' },
      component: UnifiedBundles,
      beforeEnter: authorizedUser
    },
    {
      path: '/recargas/number/*',
      name: 'CubaRecargasLegacy',
      meta: { title: 'Send Money' },
      beforeEnter (to, from, next) {
        redirectToSendMoney()
      }
    },
    {
      path: '/prebooking/:number/',
      name: 'PrebookingLegacyV2',
      meta: { title: 'Send Money' },
      beforeEnter (to, from, next) {
        redirectToSendMoney()
      }
    },
    {
      path: '/prebooking/number/*',
      name: 'PrebookingLegacy',
      meta: { title: 'Send Money' },
      beforeEnter (to, from, next) {
        redirectToSendMoney()
      }
    },
    {
      path: '/checkout/product/*',
      name: 'CheckoutLegacy',
      meta: { title: 'Send Money' },
      beforeEnter (to, from, next) {
        redirectToSendMoney()
      }
    },
    {
      path: '/checkout/receipt/*',
      name: 'CheckoutLegacyV2',
      meta: { title: 'Send Money' },
      beforeEnter (to, from, next) {
        redirectToSendMoney()
      }
    },
    {
      path: '/checkout/status/*',
      name: 'CheckoutLegacyV3',
      meta: { title: 'Send Money' },
      beforeEnter (to, from, next) {
        redirectToSendMoney()
      }
    },
    {
      path: '/checkout/recargastatus/*',
      name: 'CheckoutLegacyV4',
      meta: { title: 'Send Money' },
      beforeEnter (to, from, next) {
        redirectToSendMoney()
      }
    },
    {
      path: '/checkout/nautastatus/*',
      name: 'CheckoutLegacyV5',
      meta: { title: 'Send Money' },
      beforeEnter (to, from, next) {
        redirectToSendMoney()
      }
    },
    {
      path: '/nauta/product/*',
      name: 'CheckoutNautaLegacy',
      meta: { title: 'Send Money' },
      beforeEnter (to, from, next) {
        redirectToSendMoney()
      }
    },
    {
      path: '/nauta/receipt/*',
      name: 'CheckoutNautaLegacyV2',
      meta: { title: 'Send Money' },
      beforeEnter (to, from, next) {
        redirectToSendMoney()
      }
    },
    {
      path: '/:locale/my/contact-support/',
      name: 'ContactSupportRedirect',
      beforeEnter (to, from, next) {
        redirectHelpContactUrl(to.params.locale)
      }
    },
    {
      path: '/',
      name: 'Slash',
      beforeEnter (to, from, next) {
        next({ name: 'Account' })
      }
    },
    {
      path: '/:locale/',
      name: 'SlashLocale',
      beforeEnter (to, from, next) {
        // To fix the legacy routes, we have extra locale check below!
        if (to.params.locale === 'recargas' || to.params.locale === 'prebooking') {
          redirectToSendMoney()
        } else if (to.params.locale === 'creditmodal') {
          redirectToAddMoney()
        } else {
          next({
            name: 'Account',
            params: {
              locale: Translation.isLangSupported(to.params.locale)
                ? to.params.locale
                : Translation.defaultLanguage
            }
          })
        }
      }
    },
    {
      path: '/:locale/my/',
      name: 'SlashMy',
      beforeEnter (to, from, next) {
        next({
          name: 'Account',
          params: {
            locale: Translation.isLangSupported(to.params.locale)
              ? to.params.locale
              : Translation.defaultLanguage
          }
        })
      }
    },
    {
      path: '*',
      component: NotFound
    }
  ]
})

router.beforeEach((to, from, next) => {
  Translation.routeMiddleware(to, from, next)
})

router.onError(() => {
  router.app.$toastr.e({
    'msg': router.app.$t('errors.router_error'), // Message
    'clickClose': false, // Click Close Disable
    'timeout': 0, // Remember defaultTimeout is 5 sec..
    'preventDuplicates': true // Default is false,
  })
})

router.afterEach((to, from, next) => {
  let fragment = to.path
  // console.log('to', to)
  document.title = to.meta.title
  // i am sorry for this - timeout needs to be made in because mParticle has no way of telling us when the Identity object finished fetching
  setTimeout(() => {
    setUtmParams()
    setTikTokParams()
  }, 1000)
  if (isLoggedIn()) {
    let userId = store.getters['user/userId']
    if (!isNullOrUndefined(userId)) {
      mParticleLogin(userId.toString(), () => {
        // if condition is here because of custom events inside the components would result in duplicate event - to be solved in the future
        if (!fragment.includes('send-money') && !fragment.includes('checkout-complete')) {
          trackPageView(fragment)
          // testEvent(fragment)
        }
      })
    }
  } else {
    // we used to call the mParticle logout here, but it results in duplicate call on logout, plus everytime the user refreshes non logged in pages, it will send the request
    // mParticleLogout()
  }
})

export default router



// WEBPACK FOOTER //
// ./src/router/index.js